/**
 * Type definitions and constants for the event-based gameplay system.
 * 
 * This module provides shared constants and helper functions used across
 * all game components and hooks.
 */

/**
 * Game status values - consistent across all games.
 * These match the backend GameplayStatus enum.
 * 
 * @typedef {'not_started' | 'in_progress' | 'won' | 'lost'} GameStatus
 */
export const GameStatus = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  WON: 'won',
  LOST: 'lost',
};

/**
 * Common error codes from event responses.
 * These are returned by the backend when an event fails.
 */
export const EventErrorCode = {
  /** Game has already ended */
  GAME_OVER: 'GAME_OVER',
  /** User must be authenticated for this action */
  NOT_AUTHENTICATED: 'NOT_AUTHENTICATED',
  /** Not enough gettoncini for hint */
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  /** Hint or action already used */
  ALREADY_USED: 'ALREADY_USED',
  /** Client state is out of sync with server (retry recommended) */
  STALE_STATE: 'STALE_STATE',
  /** Session not found */
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  /** Invalid event type for this game */
  INVALID_EVENT_TYPE: 'INVALID_EVENT_TYPE',
  /** Network or connection error */
  NETWORK_ERROR: 'NETWORK_ERROR',
};

/**
 * Gettoncini transaction types.
 * These identify the type of gettoncini change in event responses.
 */
export const TransactionType = {
  /** Cost deducted for using a hint */
  HINT_COST: 'hint_cost',
  /** Reward for completing a game */
  GAME_COMPLETION: 'game_completion',
  /** Bonus for reaching a streak milestone */
  STREAK_MILESTONE: 'streak_milestone',
};

/**
 * Check if a game status represents a terminal (ended) state.
 * 
 * @param {string} status - The game status to check
 * @returns {boolean} True if the game has ended (won or lost)
 * 
 * @example
 * isGameOver(GameStatus.WON) // => true
 * isGameOver(GameStatus.IN_PROGRESS) // => false
 */
export function isGameOver(status) {
  return status === GameStatus.WON || status === GameStatus.LOST;
}

/**
 * Check if a game status represents an active (playable) state.
 * 
 * @param {string} status - The game status to check
 * @returns {boolean} True if the game can still be played
 * 
 * @example
 * isGameActive(GameStatus.IN_PROGRESS) // => true
 * isGameActive(GameStatus.WON) // => false
 */
export function isGameActive(status) {
  return status === GameStatus.NOT_STARTED || status === GameStatus.IN_PROGRESS;
}

/**
 * Check if a game was won.
 * 
 * @param {string} status - The game status to check
 * @returns {boolean} True if the game was won
 */
export function isGameWon(status) {
  return status === GameStatus.WON;
}

/**
 * Check if a game was lost.
 * 
 * @param {string} status - The game status to check
 * @returns {boolean} True if the game was lost
 */
export function isGameLost(status) {
  return status === GameStatus.LOST;
}

